import React, { MutableRefObject, useEffect, useState } from 'react';
import styled, { css } from 'styled-components';
import { transparentize } from 'polished';
import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { DEVICE_SIZE, mq } from '@marty-js/design/src/utils/mq';
import { useTranslation } from '@marty-js/design/src/utils/translation';
import { UndecoratedNavLink } from '@marty-js/design/src/atoms/link';

const ArrowDown = dynamic(() => import('@marty-js/design/src/icons/arrowDown'));
const ArrowPlainDown = dynamic(() => import('@marty-js/design/src/icons/arrowPlainDown'));

const Wrapper = styled.nav`
  background: var(--theme-color-background);
  border: 1px solid var(--theme-color-medium);
  position: relative;
  display: flex;
  flex-direction: column;
  z-index: 2;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      border: none;
      border-bottom: 1px solid var(--theme-color-medium);
      flex-direction: row;
      margin: 0 0 18px 0;
      max-height: inherit;
    `,
  )}
`;

const CollapseHeading = styled.button`
  background: var(--theme-color-background);
  border-radius: 6px;
  display: flex;
  font-family: var(--theme-typography-primary-font);
  justify-content: space-between;
  font-weight: 700;
  padding: 15px;
  text-align: left;
  font-size: 22px;
  width: 100%;

  &.mod-open {
    border-bottom: solid 1px #eeeeee;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
    z-index: 10;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

const CollapseHeadingLabel = styled.span`
  line-height: 30px;
`;

const CollapseHeadingArrow = styled.span<{ isCollapsed: boolean }>`
  transition: transform 0.3s;

  ${(props) =>
    props.isCollapsed
      ? null
      : css`
          transform: rotate(180deg);
          position: relative;
          top: 2px;
        `}
`;

const List = styled.ul`
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
  display: flex;
  flex-direction: column;
  gap: 17px;
  list-style: none;
  max-width: initial;
  max-height: 0;
  overflow-y: auto;
  width: 100%;

  &.mod-open {
    max-height: calc(100vh - 62px);
    padding: 15px;
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      align-items: center;
      flex-direction: row;
      gap: 15px;
      margin: 0;
      max-height: initial;
      max-width: 810px;
      padding: 0 !important;
      text-align: center;
    `,
  )}
`;

const ListItem = styled.li`
  flex: 1 0 auto;

  &.current {
    color: var(--theme-color-primary);
  }

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      border-bottom: solid 2px transparent;
      position: relative;

      &:not(.disabled).current,
      &:not(.disabled):hover {
        border-bottom: solid 2px var(--theme-color-primary);
        color: var(--theme-color-foreground);
        display: flex;
        text-decoration: initial;
      }

      &:last-of-type::after {
        content: initial;
      }

      &::after {
        border-right: 1px solid var(--theme-palette-dark-white);
        content: '';
        height: 30px;
        position: absolute;
        right: -9px;
        top: 50%;
        transform: translateY(-50%);
      }
    `,
  )}
`;

const ItemLink = styled.button<{ disabled: boolean }>`
  ${(props) =>
    props.disabled
      ? css`
          color: ${transparentize(0.6, props.theme.colors.foreground)};
        `
      : null}

  align-items: center;
  display: flex;
  gap: 16px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: initial;
      font-weight: bold;
      gap: initial;
      text-align: center;
      padding: 20px 0;
      width: 100%;
    `,
  )};
`;

const ItemLinkEllipse = styled.div`
  align-items: center;
  background: var(--theme-palette-dark-white);
  border-radius: 50%;
  display: flex;
  height: 24px;
  justify-content: center;
  width: 24px;

  ${mq.gte(
    DEVICE_SIZE.LARGE,
    css`
      display: none;
    `,
  )}
`;

interface BaseLink {
  label: string;
  disabled?: boolean;
}
interface DisabledLink extends BaseLink {
  disabled: true;
}
interface AnchorLink extends BaseLink {
  ref: MutableRefObject<HTMLElement>;
}
interface Link extends BaseLink {
  href: string;
}
export type MenuLink = DisabledLink | Link | AnchorLink;

interface Props {
  links: Array<MenuLink>;
  scrollOffset?: number;
  className?: string;
}
export const Menu = React.forwardRef<HTMLElement, Props>(({ links, className, scrollOffset = 0 }, ref) => {
  const [currentLinkActive, setCurrentLinkActive] = useState(0);
  const [visiblePercentage, setVisiblePercentage] = useState([]);
  const [collapsed, setCollapsed] = useState(false);
  const t = useTranslation();

  const toggleCollapse = () => {
    setCollapsed(!collapsed);
  };

  const scrollToElement = (index: number) => {
    toggleCollapse();
    const hasRef = (links[index] as AnchorLink)?.ref?.current;

    if (hasRef) {
      const elementTopPosition = hasRef.offsetTop;
      const targetElementPosition = elementTopPosition - scrollOffset;
      window.scroll({ behavior: 'smooth', top: targetElementPosition });
    }
  };

  useEffect(() => {
    const observer: IntersectionObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = links.findIndex((link) => (link as AnchorLink).ref?.current === entry.target);
          setVisiblePercentage(() => visiblePercentage.splice(index, 1, Math.floor(entry.intersectionRatio * 100)));
          const newCurrentLinkActive = visiblePercentage.indexOf(
            Math.max(...visiblePercentage.filter(Number.isFinite)),
          );
          setCurrentLinkActive(newCurrentLinkActive);
        });
      },
      {
        rootMargin: '0px',
        threshold: [0.0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1.0],
      },
    );

    links.forEach((link, index) => {
      const anchor = link as AnchorLink;
      setVisiblePercentage(visiblePercentage.splice(index, 1, 0));

      if (anchor?.ref?.current) {
        observer.observe(anchor.ref.current);
      }
    });

    return () => {
      links.forEach((link) => {
        const anchor = link as AnchorLink;

        return anchor?.ref?.current && observer.unobserve(anchor.ref.current);
      });
    };
  }, [links]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Wrapper ref={ref} className={className}>
      <CollapseHeading className={classNames({ 'mod-open': collapsed })} onClick={toggleCollapse}>
        <CollapseHeadingLabel>{t('sdk.button.summary')}</CollapseHeadingLabel>
        <CollapseHeadingArrow isCollapsed={collapsed}>
          <ArrowDown color="var(--theme-color-foreground)" height={7} width={12} />
        </CollapseHeadingArrow>
      </CollapseHeading>
      <List className={classNames({ 'mod-open': collapsed })}>
        {links.map((item, index) => (
          <ListItem
            key={item.label}
            className={classNames({ current: index === currentLinkActive, disabled: item.disabled })}
          >
            {(item as Link).href ? (
              <ItemLink as={UndecoratedNavLink} href={(item as Link).href} onClick={toggleCollapse}>
                <ItemLinkEllipse>
                  <ArrowPlainDown color="var(--theme-palette-dark-grey-blue)" height={11} width={8} />
                </ItemLinkEllipse>
                {item.label}
              </ItemLink>
            ) : (
              <ItemLink disabled={item.disabled} onClick={() => scrollToElement(index)}>
                <ItemLinkEllipse>
                  <ArrowPlainDown color="var(--theme-palette-dark-grey-blue)" height={11} width={8} />
                </ItemLinkEllipse>
                {item.label}
              </ItemLink>
            )}
          </ListItem>
        ))}
      </List>
    </Wrapper>
  );
});
